import { CERTIFICATE_SETS, type CertificateSetId } from '../domain/certificateCollection';
import { calculateDemoCollectionXp, DEMO_PRESET_CLAIMS } from './demoCertificateCollection';

export type DemoMode = 'guided' | 'interactive' | 'free';
export type DemoPreset = 'first' | 'day7' | 'almost' | 'complete' | 'parent';
export type DemoStageId = 'first-snow' | 'goal-road' | 'basecamp' | 'weather' | 'library' | 'fridge' | 'family' | 'summit';

export interface DemoStageDefinition {
  id: DemoStageId;
  order: number;
  title: string;
  subtitle: string;
  place: string;
  emotion: string;
  reward: number;
  durationSeconds: number;
  guide: 'snows' | 'haneul' | 'sage';
  asset: string;
  sceneAsset: string;
  sceneAssetMobile: string;
  actionLabel: string;
  outcome: string;
}

export interface DemoFamilyExperience {
  step: 0 | 1 | 2 | 3 | 4;
  childChoice?: string;
  encouragementId?: string;
  missionId?: string;
  completed: boolean;
  badgeClaimed: boolean;
}

export interface DemoSession {
  version: 9;
  started: boolean;
  mode: DemoMode;
  preset: DemoPreset;
  currentStage: number;
  completedStageIds: DemoStageId[];
  points: number;
  selections: Partial<Record<DemoStageId, string>>;
  autoPlaying: boolean;
  soundRequested: boolean;
  claimedCertificateIds: string[];
  claimedSetIds: CertificateSetId[];
  collectionXp: number;
  familyExperience: DemoFamilyExperience;
  startedAt?: string;
  updatedAt: string;
}

export const DEMO_STORAGE_KEY = 'mrm.demo.release2.7.session';
export const LEGACY_DEMO_STORAGE_KEYS = ['mrm.demo.release2.6.session', 'mrm.demo.release2.5.session', 'mrm.demo.release2.4.session', 'mrm.demo.release2.3.session', 'mrm.demo.release2.2.session', 'mrm.demo.release2.1.1.session', 'mrm.demo.release2.0.session'];

export const demoStages: DemoStageDefinition[] = [
  {
    id: 'first-snow', order: 1, title: '작은 눈 한 줌', subtitle: '스노스와 첫 선택을 시작해요', place: '첫눈 광장', emotion: '호기심', reward: 20, durationSeconds: 5,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/world_hub_v1.webp', sceneAssetMobile: '/assets/world-spaces/world_hub_v1_960.webp', actionLabel: '나의 탐험 목표 선택', outcome: '처음부터 정답을 요구하지 않고 작은 선택으로 시작합니다.'
  },
  {
    id: 'goal-road', order: 2, title: '나만의 눈길', subtitle: '친구와 다른 나의 목표를 정해요', place: '스노우 마운틴 입구', emotion: '자기발견', reward: 30, durationSeconds: 6,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/market_square_v1.webp', sceneAssetMobile: '/assets/world-spaces/market_square_v1_960.webp', actionLabel: '목표 스노우볼 만들기', outcome: '비교 대신 나에게 중요한 목표와 이유를 발견합니다.'
  },
  {
    id: 'basecamp', order: 3, title: '오늘의 베이스캠프', subtitle: '받고·쓰고·모은 돈을 기록해요', place: '베이스캠프', emotion: '통제감', reward: 40, durationSeconds: 7,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/basecamp_training_v1.webp', sceneAssetMobile: '/assets/world-spaces/basecamp_training_v1_960.webp', actionLabel: '오늘 돈 기록', outcome: '내 행동이 스노우볼의 방향을 바꾼다는 통제감을 얻습니다.'
  },
  {
    id: 'weather', order: 4, title: '경제기상대', subtitle: '뉴스를 날씨처럼 관찰해요', place: '경제기상대', emotion: '이해', reward: 50, durationSeconds: 8,
    guide: 'sage', asset: '/assets/characters/sage/sage_binocular_v1.png', sceneAsset: '/assets/world-spaces/news_square_v1.webp', sceneAssetMobile: '/assets/world-spaces/news_square_v1_960.webp', actionLabel: '기사 한 줄 읽고 퀴즈', outcome: '불확실한 경제 소식을 두려움이 아닌 준비의 신호로 바꿉니다.'
  },
  {
    id: 'library', order: 5, title: '사라진 경제 페이지', subtitle: '마법책의 지식 조각을 복원해요', place: '스노우볼 경제도서관', emotion: '유능감', reward: 60, durationSeconds: 8,
    guide: 'sage', asset: '/assets/characters/sage/sage_research_v1.png', sceneAsset: '/assets/world-spaces/economy_library_v1.webp', sceneAssetMobile: '/assets/world-spaces/economy_library_v1_960.webp', actionLabel: '개념 페이지 복원', outcome: '경제 개념을 외우는 대신 이야기와 현실에 연결하며 자신감을 얻습니다.'
  },
  {
    id: 'fridge', order: 6, title: '시간의 냥장고', subtitle: '오늘의 노력과 기억을 보관해요', place: '머니 냥장고', emotion: '자부심', reward: 80, durationSeconds: 7,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/snow_fridge_home_v1.webp', sceneAssetMobile: '/assets/world-spaces/snow_fridge_home_v1_960.webp', actionLabel: '오늘의 기억 카드 보관', outcome: '점수보다 내가 만든 변화와 기다림을 소유 가능한 기억으로 남깁니다.'
  },
  {
    id: 'family', order: 7, title: '가족 탐험대', subtitle: '가족의 응원과 세상의 연결을 배워요', place: '북극 항구', emotion: '연결감', reward: 100, durationSeconds: 8,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/arctic_port_v1.webp', sceneAssetMobile: '/assets/world-spaces/arctic_port_v1_960.webp', actionLabel: '가족 응원 확인', outcome: '부모가 심사자가 아니라 아이의 선택을 인정하는 탐험 동료가 됩니다.'
  },
  {
    id: 'summit', order: 8, title: '나의 미래 정상', subtitle: '오늘의 선택이 미래의 길이 돼요', place: '시간의 회랑', emotion: '회고·계승', reward: 200, durationSeconds: 9,
    guide: 'snows', asset: '/assets/characters/snows/snows_wave_v1.png', sceneAsset: '/assets/world-spaces/aurora_observatory_v1.webp', sceneAssetMobile: '/assets/world-spaces/aurora_observatory_v1_960.webp', actionLabel: '성장 리포트 완성', outcome: '성취를 돌아보고 다음 목표와 다른 사람에게 전할 경험을 발견합니다.'
  }
];

const now = () => new Date().toISOString();

export function createDemoSession(): DemoSession {
  return {
    version: 9,
    started: false,
    mode: 'interactive',
    preset: 'first',
    currentStage: 0,
    completedStageIds: [],
    points: 0,
    selections: {},
    autoPlaying: false,
    soundRequested: true,
    claimedCertificateIds: [],
    claimedSetIds: [],
    collectionXp: 0,
    familyExperience: { step: 0, completed: false, badgeClaimed: false },
    updatedAt: now(),
  };
}

const presetCount: Record<DemoPreset, number> = { first: 0, day7: 3, almost: 6, complete: 8, parent: 7 };

const presetSelections: Record<DemoStageId, string> = {
  'first-snow': '안전하게 차근차근 모으기',
  'goal-road': '가격과 기간을 먼저 계산하기',
  basecamp: '저축했어요 · 3,000원',
  weather: '가격은 여러 원인을 함께 살펴봐요',
  library: '두 자료의 날짜와 출처를 먼저 비교해요',
  fridge: '사고 싶은 것을 하루 기다린 기억',
  family: '가족 여행 계획을 함께 세우기',
  summit: '나의 경험을 가족에게 이야기하기',
};

export function sessionForPreset(preset: DemoPreset, mode: DemoMode = 'interactive', soundRequested = true): DemoSession {
  const count = presetCount[preset];
  const completed = demoStages.slice(0, count);
  const claimedCertificateIds = [...DEMO_PRESET_CLAIMS[preset]];
  return {
    ...createDemoSession(),
    started: true,
    mode,
    preset,
    currentStage: count >= demoStages.length ? demoStages.length - 1 : count,
    completedStageIds: completed.map((stage) => stage.id),
    points: completed.reduce((sum, stage) => sum + stage.reward, 0),
    selections: Object.fromEntries(completed.map((stage) => [stage.id, presetSelections[stage.id]])) as Partial<Record<DemoStageId, string>>,
    autoPlaying: mode === 'guided' && count < demoStages.length,
    soundRequested,
    claimedCertificateIds,
    claimedSetIds: [],
    collectionXp: calculateDemoCollectionXp(claimedCertificateIds, []),
    startedAt: now(),
    updatedAt: now(),
  };
}

export function normalizeDemoSession(value: unknown): DemoSession {
  if (!value || typeof value !== 'object') return createDemoSession();
  const candidate = value as Partial<DemoSession>;
  const validStageIds = new Set(demoStages.map((stage) => stage.id));
  const completed = Array.isArray(candidate.completedStageIds)
    ? candidate.completedStageIds.filter((id): id is DemoStageId => typeof id === 'string' && validStageIds.has(id as DemoStageId))
    : [];
  const uniqueCompleted = [...new Set(completed)];
  const calculatedPoints = demoStages.filter((stage) => uniqueCompleted.includes(stage.id)).reduce((sum, stage) => sum + stage.reward, 0);
  const stageIndex = typeof candidate.currentStage === 'number' && Number.isFinite(candidate.currentStage)
    ? Math.max(0, Math.min(demoStages.length - 1, Math.floor(candidate.currentStage)))
    : 0;
  const validCertificateIds = new Set(DEMO_PRESET_CLAIMS.complete.concat([
    'saving-snowball', 'patience-choice', 'price-detective', 'magic-page-restorer', 'passport-explorer', 'fridge-curator', 'basecamp-30', 'world-canon-keeper',
  ]));
  const claimedCertificateIds = Array.isArray(candidate.claimedCertificateIds)
    ? [...new Set(candidate.claimedCertificateIds.filter((id): id is string => typeof id === 'string' && validCertificateIds.has(id)))]
    : [];
  const validSetIds = new Set<CertificateSetId>(['habit', 'knowledge', 'together', 'mastery']);
  const requestedSetIds = Array.isArray(candidate.claimedSetIds)
    ? [...new Set(candidate.claimedSetIds.filter((id): id is CertificateSetId => typeof id === 'string' && validSetIds.has(id as CertificateSetId)))]
    : [];
  const claimedSetIds = requestedSetIds.filter((setId) => {
    const set = CERTIFICATE_SETS.find((item) => item.id === setId);
    return Boolean(set?.certificateIds.every((id) => claimedCertificateIds.includes(id)));
  });
  const rawFamily = candidate.familyExperience && typeof candidate.familyExperience === 'object' ? candidate.familyExperience : undefined;
  const rawStep = rawFamily && typeof rawFamily.step === 'number' ? Math.max(0, Math.min(4, Math.floor(rawFamily.step))) : 0;
  const familyExperience: DemoFamilyExperience = {
    step: rawStep as DemoFamilyExperience['step'],
    childChoice: typeof rawFamily?.childChoice === 'string' ? rawFamily.childChoice : undefined,
    encouragementId: typeof rawFamily?.encouragementId === 'string' ? rawFamily.encouragementId : undefined,
    missionId: typeof rawFamily?.missionId === 'string' ? rawFamily.missionId : undefined,
    completed: Boolean(rawFamily?.completed),
    badgeClaimed: Boolean(rawFamily?.badgeClaimed),
  };
  return {
    ...createDemoSession(),
    started: Boolean(candidate.started),
    mode: candidate.mode === 'guided' ? 'guided' : candidate.mode === 'free' ? 'free' : 'interactive',
    preset: ['first','day7','almost','complete','parent'].includes(String(candidate.preset)) ? candidate.preset as DemoPreset : 'first',
    currentStage: stageIndex,
    completedStageIds: uniqueCompleted,
    points: calculatedPoints,
    selections: candidate.selections && typeof candidate.selections === 'object' ? candidate.selections : {},
    autoPlaying: Boolean(candidate.autoPlaying),
    soundRequested: candidate.soundRequested !== false,
    claimedCertificateIds,
    claimedSetIds,
    collectionXp: calculateDemoCollectionXp(claimedCertificateIds, claimedSetIds),
    familyExperience,
    startedAt: typeof candidate.startedAt === 'string' ? candidate.startedAt : undefined,
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : now(),
  };
}

export function completeDemoStage(session: DemoSession, stageId: DemoStageId, selection = 'demo-complete'): DemoSession {
  if (session.completedStageIds.includes(stageId)) {
    return { ...session, selections: { ...session.selections, [stageId]: selection }, updatedAt: now() };
  }
  const stage = demoStages.find((item) => item.id === stageId);
  if (!stage) return session;
  return {
    ...session,
    completedStageIds: [...session.completedStageIds, stageId],
    points: session.points + stage.reward,
    selections: { ...session.selections, [stageId]: selection },
    updatedAt: now(),
  };
}

export function demoProgress(session: DemoSession) {
  const completed = session.completedStageIds.length;
  return {
    completed,
    percent: Math.round((completed / demoStages.length) * 100),
    totalPoints: demoStages.reduce((sum, stage) => sum + stage.reward, 0),
    complete: completed === demoStages.length,
  };
}
